import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Percent, Plus, Scale } from 'lucide-react';
import { useFounders } from './hooks/useFounders';
import {
  BalanceCheckDialog,
  BalanceChecksTable,
  FounderCards,
  SharesDialog,
  TransactionDialog,
  TransactionsTable,
  TreasurySummary,
} from './components';
import { FoundersPageProps, FoundersTab } from './types';

/** Вкладки нижнего блока: движения денег и сверка кассы с банком. */
const TABS: Array<{ key: FoundersTab; title: string; hint: string }> = [
  {
    key: 'transactions',
    title: 'Движения',
    hint: 'Взносы на счёт, возвраты вкладов и доли прибыли. Расходы из кармана сюда не попадают — они уже учтены на экране «Расходы»',
  },
  {
    key: 'checks',
    title: 'Остатки по выписке',
    hint: 'Сколько денег реально было на счету на дату. Расхождение с кассой означает забытый расход или движение',
  },
];

export default function FoundersPage({ isDark }: FoundersPageProps) {
  const {
    overview,
    payers,
    isLoading,
    isSubmitting,
    isTransactionDialogOpen,
    setIsTransactionDialogOpen,
    isSharesDialogOpen,
    setIsSharesDialogOpen,
    isBalanceDialogOpen,
    setIsBalanceDialogOpen,
    editingTransaction,
    transactionForm,
    setTransactionForm,
    balanceForm,
    setBalanceForm,
    openCreateTransaction,
    openEditTransaction,
    submitTransaction,
    deleteTransaction,
    openBalanceCheck,
    submitBalanceCheck,
    deleteBalanceCheck,
    updateShare,
  } = useFounders();

  const [tab, setTab] = useState<FoundersTab>('transactions');

  const formatMoney = (amount: number) =>
    new Intl.NumberFormat('uz-UZ', {
      style: 'currency',
      currency: 'UZS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const founderPayers = useMemo(() => payers.filter((p) => !p.is_company), [payers]);

  const debtByFounder = useMemo(
    () =>
      Object.fromEntries((overview?.founders || []).map((f) => [f.payerId, f.debt])) as Record<
        string,
        number
      >,
    [overview],
  );

  const profitAvailableByFounder = useMemo(
    () =>
      Object.fromEntries(
        (overview?.founders || []).map((f) => [f.payerId, f.profitAvailable]),
      ) as Record<string, number>,
    [overview],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>
            Основатели
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Кто сколько вложил, сколько компания должна вернуть и что можно делить
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setIsSharesDialogOpen(true)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            <Percent className="w-4 h-4 mr-2" />
            Доли
          </Button>
          <Button
            onClick={tab === 'checks' ? openBalanceCheck : openCreateTransaction}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            {tab === 'checks' ? (
              <Scale className="w-4 h-4 mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            {tab === 'checks' ? 'Записать остаток' : 'Добавить движение'}
          </Button>
        </div>
      </div>

      <TreasurySummary
        isDark={isDark}
        overview={overview}
        isLoading={isLoading}
        formatMoney={formatMoney}
      />

      <FounderCards
        isDark={isDark}
        founders={overview?.founders || []}
        formatMoney={formatMoney}
      />

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden transition-all duration-300',
          isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        <div className={cn('p-5 lg:p-6 border-b', isDark ? 'border-slate-700' : 'border-slate-200')}>
          <div className="flex flex-wrap items-center gap-2">
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={cn(
                  'px-4 py-2 rounded-xl text-sm font-medium transition-colors',
                  tab === item.key
                    ? 'bg-[#C9A227] text-[#111111]'
                    : isDark
                      ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                )}
              >
                {item.title} (
                {item.key === 'transactions'
                  ? overview?.transactions.length ?? 0
                  : overview?.checks.length ?? 0}
                )
              </button>
            ))}
          </div>
          <p className={cn('text-sm mt-3', isDark ? 'text-slate-400' : 'text-slate-500')}>
            {TABS.find((item) => item.key === tab)?.hint}
          </p>
        </div>

        {tab === 'transactions' ? (
          <TransactionsTable
            isDark={isDark}
            transactions={overview?.transactions || []}
            isLoading={isLoading}
            onEdit={openEditTransaction}
            onDelete={deleteTransaction}
            formatMoney={formatMoney}
          />
        ) : (
          <BalanceChecksTable
            isDark={isDark}
            checks={overview?.checks || []}
            isLoading={isLoading}
            onDelete={deleteBalanceCheck}
            formatMoney={formatMoney}
          />
        )}
      </Card>

      <TransactionDialog
        isDark={isDark}
        isOpen={isTransactionDialogOpen}
        onOpenChange={setIsTransactionDialogOpen}
        founders={founderPayers}
        formData={transactionForm}
        onFormDataChange={setTransactionForm}
        onSubmit={submitTransaction}
        isEditing={Boolean(editingTransaction)}
        isSubmitting={isSubmitting}
        debtByFounder={debtByFounder}
        profitAvailableByFounder={profitAvailableByFounder}
        formatMoney={formatMoney}
      />

      <SharesDialog
        isDark={isDark}
        isOpen={isSharesDialogOpen}
        onOpenChange={setIsSharesDialogOpen}
        founders={founderPayers}
        onUpdate={updateShare}
      />

      <BalanceCheckDialog
        isDark={isDark}
        isOpen={isBalanceDialogOpen}
        onOpenChange={setIsBalanceDialogOpen}
        formData={balanceForm}
        onFormDataChange={setBalanceForm}
        onSubmit={submitBalanceCheck}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
